import React, {useMemo} from 'react';
import {ButtonDropdownField, DropdownList, FlexColumn, FlexRow, Icon, Label} from '@forecast-it/design-system';
import {FormattedMessage, useIntl} from 'react-intl';
import {useReportBuilder} from './ReportBuilderContext';
import {GroupingToken} from './ReportTable/useClientGrouping';

/** grouping tokens in a fixed presentation order (does not imply selection order — the
 * user's chosen order is `grouping`, this is only the order options are offered in). `spint` is
 * the legacy backend spelling for 'sprint' (see useClientGrouping.ts) and is intentionally
 * labelled 'Sprint' here, never exposed to the user as 'spint'. */
const GROUPING_TOKEN_ORDER: GroupingToken[] = ['project', 'phase', 'client', 'role', 'spint', 'task', 'assignee'];

export const GROUPING_TOKEN_LABEL_KEY: Record<GroupingToken, string> = {
	project: 'ai_report_builder.group_by_token.project',
	phase: 'ai_report_builder.group_by_token.phase',
	client: 'ai_report_builder.group_by_token.client',
	role: 'ai_report_builder.group_by_token.role',
	spint: 'ai_report_builder.group_by_token.sprint',
	task: 'ai_report_builder.group_by_token.task',
	assignee: 'ai_report_builder.group_by_token.assignee',
};

/** definition contract caps grouping at 4 ordered levels (see useClientGrouping.ts's `groupRows`). */
const MAX_GROUPING_TOKENS = 4;

/** Ordered multi-select of up to 4 grouping tokens. Selected tokens render as removable rows (in
 * selection order); a trailing "add group level" trigger offers the remaining tokens and is
 * hidden once the cap is reached. Operates purely through `useReportBuilder()`'s
 * `grouping`/`setGrouping` — it holds no local selection state of its own. */
export function GroupByControl(): JSX.Element {
	const {formatMessage} = useIntl();
	const {grouping, setGrouping} = useReportBuilder();

	const availableTokens = useMemo(() => GROUPING_TOKEN_ORDER.filter(token => !grouping.includes(token)), [grouping]);

	const atCap = grouping.length >= MAX_GROUPING_TOKENS;

	const addToken = (token: GroupingToken): void => {
		if (atCap || grouping.includes(token)) {
			return;
		}
		setGrouping([...grouping, token]);
	};

	const removeToken = (token: GroupingToken): void => {
		setGrouping(grouping.filter(t => t !== token));
	};

	return (
		<FlexColumn gap={'xs'} flex={'0 0 auto'}>
			{/* the design's "Group By" control is a labeled `Dropdown field` instance
			 * (componentProperties `Label Show: true`, `Label text: 'Group By'`) — a visible caption
			 * above a bordered field-style trigger, not a bare button.
			 * `ButtonDropdownField` is the DS component for the field itself; the caption is a
			 * sibling `Label`, matching the `FlexColumn`-wrapped `Label` + field pattern used
			 * elsewhere (e.g. ChangeViewGroupings.tsx's `select-grouping` field). This also
			 * differentiates Group By's trigger from Columns', which stays a plain `ButtonDropdown`
			 * per the design's `Filter` component instance. */}
			<Label data-cy={'ai-report-group-by-label'}>
				<FormattedMessage id={'ai_report_builder.group_by_label'} />
			</Label>
			<DropdownList>
				<DropdownList.Trigger>
					{/* the design: sizes the Group By field at ~180px, not full-bleed;
					 * `ButtonDropdownField` defaults to width 100%, so pin it explicitly. */}
					<ButtonDropdownField width={'180px'} isDefaultLabel={grouping.length === 0} data-cy={'ai-report-group-by'}>
						<FormattedMessage id={'ai_report_builder.group_by'} />
					</ButtonDropdownField>
				</DropdownList.Trigger>
				<DropdownList.Content offset={4}>
					<DropdownList.ContentGroup title={formatMessage({id: 'ai_report_builder.group_by'})}>
						{grouping.map((token, index) => (
							<DropdownList.Item
								key={token}
								onClick={() => removeToken(token)}
								data-cy={`ai-report-group-by-selected-${token}`}
							>
								<FlexRow justifyContent={'space-between'} alignItems={'center'}>
									<span>
										{index + 1}. <FormattedMessage id={GROUPING_TOKEN_LABEL_KEY[token]} />
									</span>
									<Icon icon={'close'} />
								</FlexRow>
							</DropdownList.Item>
						))}
						{!atCap && availableTokens.length > 0 ? (
							<DropdownList.DropdownItem>
								<DropdownList.Trigger>
									<ButtonDropdownField isDefaultLabel data-cy={'ai-report-group-by-add'}>
										<FormattedMessage id={'ai_report_builder.group_by_add'} />
									</ButtonDropdownField>
								</DropdownList.Trigger>
								<DropdownList.Content offset={4} useTriggerWidth>
									{availableTokens.map(token => (
										<DropdownList.Item
											key={token}
											onClick={() => addToken(token)}
											data-cy={`ai-report-group-by-option-${token}`}
										>
											<FormattedMessage id={GROUPING_TOKEN_LABEL_KEY[token]} />
										</DropdownList.Item>
									))}
								</DropdownList.Content>
							</DropdownList.DropdownItem>
						) : null}
					</DropdownList.ContentGroup>
				</DropdownList.Content>
			</DropdownList>
		</FlexColumn>
	);
}
